import React, { useState, useEffect } from 'react';
import { useEntity } from '../contexts/EntityContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { FileText, Calendar, DollarSign, AlertTriangle, CheckCircle, ExternalLink } from 'lucide-react';

import { API_URL } from '../config/api';

export function TaxInstructions() {
  const { selectedEntity, isUSEntity } = useEntity();
  const [instructions, setInstructions] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (selectedEntity && isUSEntity) {
      fetchInstructions();
    }
  }, [selectedEntity, isUSEntity]);

  const fetchInstructions = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/us-tax-instructions/${selectedEntity.entity_id}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setInstructions(data);
      }
    } catch (error) {
      console.error('Failed to fetch tax instructions:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  if (!isUSEntity) {
    return (
      <div className="animate-fade-in">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">
              Tax instructions are only available for US entities. For Dutch entities, see VAT Returns.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          US Tax Filing Instructions
        </h1>
        <p className="text-muted-foreground">
          {selectedEntity?.name} - Tax Year {instructions?.tax_year || new Date().getFullYear() - 1}
        </p>
      </div>

      {loading ? (
        <div className="py-12 text-center">
          <div className="w-8 h-8 border-2 border-[hsl(var(--brand))] border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      ) : instructions ? (
        <>
          {/* Summary Card */}
          <Card className="border-[hsl(var(--brand))]/30 bg-[hsl(var(--brand))]/5">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[hsl(var(--brand))] rounded-lg flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Estimated Federal Tax Due
                  </h3>
                  <p className="text-3xl font-bold text-[hsl(var(--brand))] mt-1">
                    {formatCurrency(instructions.estimated_federal_tax)}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Based on 21% corporate tax rate
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Important Deadlines */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Calendar className="w-5 h-5 text-amber-500" />
                Important Deadlines
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {instructions.important_deadlines?.map((deadline, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
                    <div>
                      <p className="font-medium">{deadline.deadline}</p>
                      <p className="text-sm text-muted-foreground">{deadline.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Forms Required */}
          {instructions.forms_required?.map((form, formIdx) => (
            <Card key={formIdx}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <FileText className="w-5 h-5" />
                    {form.form}
                  </CardTitle>
                  <Badge variant="outline">{form.due_date}</Badge>
                </div>
                <p className="text-sm text-muted-foreground">{form.name}</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {form.instructions?.map((instruction, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center justify-between py-3 border-b border-border last:border-0"
                    >
                      <div className="flex items-center gap-3">
                        <span className="w-16 font-mono text-sm text-muted-foreground">
                          {instruction.line}
                        </span>
                        <div>
                          <p className="font-medium text-sm">{instruction.description}</p>
                          <p className="text-xs text-muted-foreground">{instruction.source}</p>
                        </div>
                      </div>
                      <span className="font-mono font-semibold">
                        {typeof instruction.value === 'number' 
                          ? formatCurrency(instruction.value)
                          : instruction.value}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}

          {/* State Tax Notice */}
          <Card className="border-dashed">
            <CardContent className="py-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-2xl">
                  🇺🇸
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    State Tax Requirements
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    {instructions.state_tax_notes}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <a 
                      href="https://www.irs.gov/forms-pubs/about-form-1120"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-[hsl(var(--brand))] hover:underline inline-flex items-center gap-1"
                    >
                      IRS Form 1120 Instructions <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Checklist */}
          <Card>
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                Filing Checklist
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  'Gather all income records and bank statements',
                  'Compile expense receipts and invoices',
                  'Calculate depreciation for business assets',
                  'Review officer compensation records',
                  'Check for any estimated tax payments made',
                  'Review state tax obligations',
                  'Consider Form 7004 extension if needed'
                ].map((item, idx) => (
                  <label key={idx} className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" className="w-4 h-4 rounded" />
                    <span className="text-sm">{item}</span>
                  </label>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">
              Add some transactions to generate tax filing instructions.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
